import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import mapboxgl from "mapbox-gl";
import {
  getPipelineGeoJSON,
  getNodesGeoJSON,
  getAlertsGeoJSON,
  generateNodes,
  generateAlerts,
  pipelineSegments,
  type Alert,
} from "../data/pipelineData";

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

type ThemeMode = "light" | "dark";

interface UseMapOptions {
  container: React.RefObject<HTMLDivElement | null>;
  themeMode?: ThemeMode;
  center?: [number, number];
  zoom?: number;
  pitch?: number;
  bearing?: number;
}

export interface UseMapResult {
  map: mapboxgl.Map | null;
  isLoaded: boolean;
  mapError: string | null;
  alerts: Alert[];
  flyToAlert: (alert: Alert) => void;
  isDroneMode: boolean;
  startDroneAnimation: () => void;
  stopDroneAnimation: () => void;
  resetView: () => void;
  nodesVisible: boolean;
  setNodesVisible: (visible: boolean) => void;
}

const DEFAULT_CENTER: [number, number] = [-2.0, 54.0]; // UK center
const DEFAULT_ZOOM = 5.5;
const DEFAULT_PITCH = 40;
const DEFAULT_BEARING = 0;

const MAP_STYLES: Record<ThemeMode, string> = {
  light: "mapbox://styles/mapbox/light-v11",
  dark: "mapbox://styles/mapbox/dark-v11",
};

const ALERT_SEVERITY_COLORS: Record<string, string> = {
  critical: "#ef4444",
  high: "#f97316",
  medium: "#eab308",
  low: "#22c55e",
};

const RIPPLE_LAYERS_COUNT = 3;

// Helper to create circular polygon from point for 3D extrusion
const createCirclePolygon = (
  center: [number, number],
  radiusKm: number,
  segments: number = 32
): [number, number][] => {
  const coords: [number, number][] = [];
  const [lng, lat] = center;

  // Convert radius from km to degrees (approximate)
  const latRadius = radiusKm / 111.32;
  const lngRadius = radiusKm / (111.32 * Math.cos((lat * Math.PI) / 180));

  for (let i = 0; i <= segments; i++) {
    const angle = (i / segments) * 2 * Math.PI;
    coords.push([
      lng + lngRadius * Math.cos(angle),
      lat + latRadius * Math.sin(angle),
    ]);
  }
  return coords;
};

// Generate pole polygons from nodes
const getNodeCylindersGeoJSON = (nodes: ReturnType<typeof generateNodes>): GeoJSON.FeatureCollection => ({
  type: "FeatureCollection",
  features: nodes.map((node) => {
    // Thin pole radius
    const radius = node.type === "sensor" ? 0.015 : node.type === "compressor" ? 0.03 : 0.035;
    // Pole heights (80% of previous)
    const height = node.type === "sensor" ? 240 : node.type === "compressor" ? 640 : 800;

    return {
      type: "Feature" as const,
      properties: {
        id: node.id,
        name: node.name,
        type: node.type,
        status: node.status,
        height,
        batteryLevel: node.batteryLevel,
        signalStrength: node.signalStrength,
      },
      geometry: {
        type: "Polygon" as const,
        coordinates: [createCirclePolygon(node.coordinates, radius, 16)],
      },
    };
  }),
});

export const useMap = ({
  container,
  themeMode = "dark",
  center = DEFAULT_CENTER,
  zoom = DEFAULT_ZOOM,
  pitch = DEFAULT_PITCH,
  bearing = DEFAULT_BEARING,
}: UseMapOptions): UseMapResult => {
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [isDroneMode, setIsDroneMode] = useState(false);
  const [nodesVisible, setNodesVisibleState] = useState(true);
  const nodesVisibleRef = useRef(true);
  const initializedRef = useRef(false);
  const animationRef = useRef<number | null>(null);
  const rippleAnimationRef = useRef<number | null>(null);
  const droneAnimationRef = useRef<number | null>(null);
  const dronePathRef = useRef<[number, number][]>([]);
  const droneIndexRef = useRef(0);
  const currentThemeRef = useRef<ThemeMode>(themeMode);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  // Store generated data in refs so they persist across style changes
  const nodesDataRef = useRef<ReturnType<typeof generateNodes> | null>(null);
  const pipelineDataRef = useRef<GeoJSON.FeatureCollection | null>(null);
  const nodesGeoJSONRef = useRef<GeoJSON.FeatureCollection | null>(null);
  const nodeCylindersDataRef = useRef<GeoJSON.FeatureCollection | null>(null);
  const alertsDataRef = useRef<GeoJSON.FeatureCollection | null>(null);
  const isFirstStyleLoadRef = useRef(true);

  const alerts = useMemo(() => generateAlerts(), []);

  const flyToAlert = useCallback((alert: Alert) => {
    if (!mapRef.current) return;

    mapRef.current.flyTo({
      center: alert.coordinates,
      zoom: 15,
      pitch: 50,
      bearing: 20,
      duration: 2000,
      essential: true,
    });
  }, []);

  // Build complete drone path from all pipeline segments
  const buildDronePath = useCallback((): [number, number][] => {
    const path: [number, number][] = [];

    // Interpolate points along each segment for smoother animation
    pipelineSegments.forEach((segment) => {
      const coords = segment.coordinates;
      for (let i = 0; i < coords.length - 1; i++) {
        const start = coords[i];
        const end = coords[i + 1];

        // Calculate distance-based steps for consistent speed
        const distance = Math.sqrt(
          Math.pow(end[0] - start[0], 2) + Math.pow(end[1] - start[1], 2)
        );
        // More steps for longer segments for smooth consistent speed
        const steps = Math.max(50, Math.floor(distance * 500));

        for (let j = 0; j <= steps; j++) {
          const t = j / steps;
          path.push([
            start[0] + (end[0] - start[0]) * t,
            start[1] + (end[1] - start[1]) * t,
          ]);
        }
      }
    });

    return path;
  }, []);

  const startDroneAnimation = useCallback(() => {
    if (!mapRef.current) return;

    // Build path if not already built
    if (dronePathRef.current.length === 0) {
      dronePathRef.current = buildDronePath();
    }

    setIsDroneMode(true);

    const map = mapRef.current;
    const path = dronePathRef.current;

    // Animation state
    let currentIndex = 0;
    let progress = 0; // 0 to 1 between current and next point
    let lastTime = performance.now();
    let currentBearing = 0;

    // Speed: how much progress per millisecond (lower = slower)
    const speed = 0.001;

    // Smoothly interpolate bearing to avoid jerky rotation
    const lerpAngle = (from: number, to: number, t: number) => {
      // Handle angle wrapping
      let diff = to - from;
      if (diff > 180) diff -= 360;
      if (diff < -180) diff += 360;
      return from + diff * t;
    };

    const animateDrone = (currentTime: number) => {
      if (!mapRef.current) return;

      const deltaTime = currentTime - lastTime;
      lastTime = currentTime;

      // Update progress
      progress += speed * deltaTime;

      // Move to next segment when progress >= 1
      while (progress >= 1 && currentIndex < path.length - 2) {
        progress -= 1;
        currentIndex++;
      }

      // Loop back to start
      if (currentIndex >= path.length - 2) {
        currentIndex = 0;
        progress = 0;
      }

      const currentPoint = path[currentIndex];
      const nextPoint = path[currentIndex + 1];

      // Interpolate position smoothly
      const lng = currentPoint[0] + (nextPoint[0] - currentPoint[0]) * progress;
      const lat = currentPoint[1] + (nextPoint[1] - currentPoint[1]) * progress;

      // Calculate target bearing (look ahead for smoother turns)
      const lookAheadIndex = Math.min(currentIndex + 20, path.length - 1);
      const lookAheadPoint = path[lookAheadIndex];
      const targetBearing = Math.atan2(
        lookAheadPoint[0] - lng,
        lookAheadPoint[1] - lat
      ) * (180 / Math.PI);

      // Smooth bearing interpolation
      currentBearing = lerpAngle(currentBearing, targetBearing, 0.02);

      // Update camera position directly for smooth movement
      map.setCenter([lng, lat]);
      map.setBearing(currentBearing);

      droneAnimationRef.current = requestAnimationFrame(animateDrone);
    };

    // Initial fly to start position
    const startPoint = path[0];
    map.flyTo({
      center: startPoint,
      zoom: 15.5,
      pitch: 70,
      bearing: 0,
      duration: 2500,
    });

    // Start animation after initial fly
    setTimeout(() => {
      lastTime = performance.now();
      droneAnimationRef.current = requestAnimationFrame(animateDrone);
    }, 2600);
  }, [buildDronePath]);

  const stopDroneAnimation = useCallback(() => {
    if (droneAnimationRef.current) {
      cancelAnimationFrame(droneAnimationRef.current);
      droneAnimationRef.current = null;
    }
    setIsDroneMode(false);
  }, []);

  const resetView = useCallback(() => {
    stopDroneAnimation();

    if (!mapRef.current) return;

    mapRef.current.flyTo({
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      pitch: DEFAULT_PITCH,
      bearing: DEFAULT_BEARING,
      duration: 2000,
      essential: true,
    });
  }, [stopDroneAnimation]);

  const setNodesVisible = useCallback((visible: boolean) => {
    nodesVisibleRef.current = visible;
    setNodesVisibleState(visible);

    if (!mapRef.current) return;

    const map = mapRef.current;
    const visibility = visible ? "visible" : "none";

    // Toggle visibility for all node-related layers
    const nodeLayers = ["nodes-3d", "nodes-main", "nodes-labels"];
    nodeLayers.forEach((layerId) => {
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, "visibility", visibility);
      }
    });
  }, []);

  // Function to add all custom sources and layers to the map
  const addCustomLayers = useCallback((map: mapboxgl.Map, mode: ThemeMode) => {
    // Generate or reuse nodes data
    if (!nodesDataRef.current) {
      nodesDataRef.current = generateNodes();
    }
    if (!pipelineDataRef.current) {
      pipelineDataRef.current = getPipelineGeoJSON();
    }
    if (!nodesGeoJSONRef.current) {
      nodesGeoJSONRef.current = getNodesGeoJSON(nodesDataRef.current);
    }
    if (!nodeCylindersDataRef.current) {
      nodeCylindersDataRef.current = getNodeCylindersGeoJSON(nodesDataRef.current);
    }
    if (!alertsDataRef.current) {
      alertsDataRef.current = getAlertsGeoJSON(alerts);
    }

    const nodes = nodesDataRef.current;
    const pipelineData = pipelineDataRef.current;
    const nodesData = nodesGeoJSONRef.current;
    const nodeCylindersData = nodeCylindersDataRef.current;
    const alertsData = alertsDataRef.current;

    // Add pipeline source
    if (!map.getSource("pipelines")) {
      map.addSource("pipelines", {
        type: "geojson",
        data: pipelineData,
      });
    }

    // Add nodes source
    if (!map.getSource("nodes")) {
      map.addSource("nodes", {
        type: "geojson",
        data: nodesData,
      });
    }

    // Add node cylinders source
    if (!map.getSource("node-cylinders")) {
      map.addSource("node-cylinders", {
        type: "geojson",
        data: nodeCylindersData,
      });
    }

    // Add alerts source
    if (!map.getSource("alerts")) {
      map.addSource("alerts", {
        type: "geojson",
        data: alertsData,
      });
    }

    // Pipeline main layer
    if (!map.getLayer("pipeline-main")) {
      map.addLayer(
        {
          id: "pipeline-main",
          type: "line",
          source: "pipelines",
          layout: {
            "line-join": "round",
            "line-cap": "round",
          },
          paint: {
            "line-color": [
              "match",
              ["get", "region"],
              "scotland_north", "#3b82f6",
              "west", "#22c55e",
              "east", "#f59e0b",
              "#00ffff"
            ],
            "line-width": 6,
            "line-opacity": 0.9,
          },
        },
        "waterway-label"
      );
    }

    // Pipeline animated ripple layer
    if (!map.getLayer("pipeline-flow")) {
      map.addLayer(
        {
          id: "pipeline-flow",
          type: "line",
          source: "pipelines",
          layout: {
            "line-join": "round",
            "line-cap": "round",
          },
          paint: {
            "line-color": "#ffffff",
            "line-width": 2,
            "line-opacity": 0.7,
            "line-dasharray": [1, 3],
          },
        },
        "waterway-label"
      );
    }

    // Determine node visibility based on current state
    const nodeVisibility = nodesVisibleRef.current ? "visible" : "none";

    // 3D Node cylinders
    if (!map.getLayer("nodes-3d")) {
      map.addLayer({
        id: "nodes-3d",
        type: "fill-extrusion",
        source: "node-cylinders",
        minzoom: 8,
        layout: {
          visibility: nodeVisibility,
        },
        paint: {
          "fill-extrusion-color": [
            "match",
            ["get", "status"],
            "online", [
              "match",
              ["get", "type"],
              "compressor", "#8b5cf6",
              "terminal", "#ec4899",
              "agi", "#06b6d4",
              "#22c55e"
            ],
            "degraded", "#f59e0b",
            "#ef4444"
          ],
          "fill-extrusion-height": ["get", "height"],
          "fill-extrusion-base": 0,
          "fill-extrusion-opacity": 0.85,
        },
      });
    }

    // Node top caps (circles at low zoom, hidden at high zoom where 3D shows)
    if (!map.getLayer("nodes-main")) {
      map.addLayer({
        id: "nodes-main",
        type: "circle",
        source: "nodes",
        maxzoom: 10,
        layout: {
          visibility: nodeVisibility,
        },
        paint: {
          "circle-radius": [
            "interpolate",
            ["linear"],
            ["zoom"],
            5, ["match", ["get", "type"], "sensor", 2, 4],
            8, ["match", ["get", "type"], "sensor", 4, 6],
            10, ["match", ["get", "type"], "sensor", 6, 10],
          ],
          "circle-color": [
            "match",
            ["get", "status"],
            "online", [
              "match",
              ["get", "type"],
              "compressor", "#8b5cf6",
              "terminal", "#ec4899",
              "agi", "#06b6d4",
              "#22c55e"
            ],
            "degraded", "#f59e0b",
            "#ef4444"
          ],
          "circle-stroke-width": 1.5,
          "circle-stroke-color": "#ffffff",
          "circle-stroke-opacity": 0.8,
        },
      });
    }

    // Node labels for important nodes
    if (!map.getLayer("nodes-labels")) {
      map.addLayer({
        id: "nodes-labels",
        type: "symbol",
        source: "nodes",
        filter: ["in", ["get", "type"], ["literal", ["compressor", "terminal"]]],
        layout: {
          "text-field": ["get", "name"],
          "text-font": ["DIN Pro Medium", "Arial Unicode MS Regular"],
          "text-size": 11,
          "text-offset": [0, 1.5],
          "text-anchor": "top",
          visibility: nodeVisibility,
        },
        paint: {
          "text-color": "#ffffff",
          "text-halo-color": "rgba(0,0,0,0.8)",
          "text-halo-width": 1,
        },
      });
    }

    // Add ripple layers for alerts (multiple layers for staggered animation)
    for (let i = 0; i < RIPPLE_LAYERS_COUNT; i++) {
      if (!map.getLayer(`alerts-ripple-${i}`)) {
        map.addLayer({
          id: `alerts-ripple-${i}`,
          type: "circle",
          source: "alerts",
          paint: {
            "circle-radius": [
              "interpolate",
              ["linear"],
              ["zoom"],
              5, 8,
              8, 12,
              12, 20,
            ],
            "circle-color": [
              "match",
              ["get", "severity"],
              "critical", ALERT_SEVERITY_COLORS.critical,
              "high", ALERT_SEVERITY_COLORS.high,
              "medium", ALERT_SEVERITY_COLORS.medium,
              "low", ALERT_SEVERITY_COLORS.low,
              ALERT_SEVERITY_COLORS.medium,
            ],
            "circle-opacity": 0.4,
            "circle-stroke-width": 0,
          },
        });
      }
    }

    // Alert center point (solid dot)
    if (!map.getLayer("alerts-center")) {
      map.addLayer({
        id: "alerts-center",
        type: "circle",
        source: "alerts",
        paint: {
          "circle-radius": [
            "interpolate",
            ["linear"],
            ["zoom"],
            5, 5,
            8, 7,
            12, 10,
          ],
          "circle-color": [
            "match",
            ["get", "severity"],
            "critical", ALERT_SEVERITY_COLORS.critical,
            "high", ALERT_SEVERITY_COLORS.high,
            "medium", ALERT_SEVERITY_COLORS.medium,
            "low", ALERT_SEVERITY_COLORS.low,
            ALERT_SEVERITY_COLORS.medium,
          ],
          "circle-opacity": 1,
          "circle-stroke-width": 2,
          "circle-stroke-color": "#ffffff",
          "circle-stroke-opacity": 0.9,
        },
      });
    }

    // Alert icon symbol
    if (!map.getLayer("alerts-icon")) {
      map.addLayer({
        id: "alerts-icon",
        type: "symbol",
        source: "alerts",
        layout: {
          "text-field": "!",
          "text-font": ["DIN Pro Bold", "Arial Unicode MS Bold"],
          "text-size": [
            "interpolate",
            ["linear"],
            ["zoom"],
            5, 10,
            8, 12,
            12, 14,
          ],
          "text-allow-overlap": true,
        },
        paint: {
          "text-color": "#ffffff",
        },
      });
    }

    // Add 3D building extrusions
    if (!map.getLayer("3d-buildings")) {
      const layers = map.getStyle().layers;
      const labelLayerId = layers?.find(
        (layer) => layer.type === "symbol" && layer.layout?.["text-field"]
      )?.id;

      map.addLayer(
        {
          id: "3d-buildings",
          source: "composite",
          "source-layer": "building",
          filter: ["==", "extrude", "true"],
          type: "fill-extrusion",
          minzoom: 12,
          paint: {
            "fill-extrusion-color": [
              "interpolate",
              ["linear"],
              ["get", "height"],
              0,
              "#1a1a2e",
              50,
              "#16213e",
              100,
              "#0f3460",
              200,
              "#1a1a4e",
            ],
            "fill-extrusion-height": ["get", "height"],
            "fill-extrusion-base": ["get", "min_height"],
            "fill-extrusion-opacity": 0.7,
          },
        },
        labelLayerId
      );
    }

    // Add atmosphere effect based on theme
    if (mode === "light") {
      map.setFog({
        color: "rgb(220, 230, 240)",
        "high-color": "rgb(135, 180, 220)",
        "horizon-blend": 0.1,
        "space-color": "rgb(180, 210, 240)",
        "star-intensity": 0,
      });
    } else {
      map.setFog({
        color: "rgb(15, 15, 25)",
        "high-color": "rgb(25, 25, 45)",
        "horizon-blend": 0.08,
        "space-color": "rgb(5, 5, 15)",
        "star-intensity": 0.2,
      });
    }
  }, [alerts]);

  // Start animations for the map
  const startAnimations = useCallback((map: mapboxgl.Map) => {
    // Cancel any existing animations
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    if (rippleAnimationRef.current) {
      cancelAnimationFrame(rippleAnimationRef.current);
    }

    // Animate ripple effect
    let ripplePhase = 0;
    const animateRipple = () => {
      if (!map.getLayer("alerts-ripple-0")) {
        rippleAnimationRef.current = requestAnimationFrame(animateRipple);
        return;
      }

      ripplePhase = (ripplePhase + 0.02) % 1;

      for (let i = 0; i < RIPPLE_LAYERS_COUNT; i++) {
        const layerId = `alerts-ripple-${i}`;
        if (!map.getLayer(layerId)) continue;

        const phase = (ripplePhase + i / RIPPLE_LAYERS_COUNT) % 1;

        const baseRadius = map.getZoom() < 7 ? 8 : map.getZoom() < 10 ? 12 : 20;
        const maxRadius = baseRadius * 3;
        const radius = baseRadius + (maxRadius - baseRadius) * phase;

        const opacity = 0.5 * (1 - phase);

        map.setPaintProperty(layerId, "circle-radius", radius);
        map.setPaintProperty(layerId, "circle-opacity", opacity);
      }

      rippleAnimationRef.current = requestAnimationFrame(animateRipple);
    };
    animateRipple();

    // Animate pipeline ripple flow
    let dashPhase = 0;
    const animatePipeline = () => {
      if (!map.getLayer("pipeline-flow")) {
        animationRef.current = requestAnimationFrame(animatePipeline);
        return;
      }

      dashPhase = (dashPhase + 0.15) % 4;

      // Moving dash pattern for ripple effect
      map.setPaintProperty("pipeline-flow", "line-dasharray", [
        dashPhase,
        3,
      ]);

      animationRef.current = requestAnimationFrame(animatePipeline);
    };
    animatePipeline();
  }, []);

  useEffect(() => {
    if (initializedRef.current || !container.current || mapRef.current) {
      return;
    }

    initializedRef.current = true;
    currentThemeRef.current = themeMode;

    try {
      const map = new mapboxgl.Map({
        container: container.current,
        style: MAP_STYLES[themeMode],
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        pitch: DEFAULT_PITCH,
        bearing: DEFAULT_BEARING,
        antialias: true,
        attributionControl: false,
      });

      map.addControl(new mapboxgl.ScaleControl(), "bottom-right");

      map.on("load", () => {
        // Add all custom sources and layers
        addCustomLayers(map, currentThemeRef.current);

        // Start animations
        startAnimations(map);

        // Click handler for alerts
        map.on("click", "alerts-center", (e) => {
          if (!e.features?.[0]) return;

          const feature = e.features[0];
          const props = feature.properties;
          if (!props) return;
          const coords = (feature.geometry as GeoJSON.Point).coordinates;

          const severityColor = ALERT_SEVERITY_COLORS[props.severity] || ALERT_SEVERITY_COLORS.medium;
          const timestamp = new Date(props.timestamp).toLocaleString();

          new mapboxgl.Popup({
            closeButton: true,
            closeOnClick: true,
            className: "alert-popup",
            maxWidth: "300px",
          })
            .setLngLat(coords as [number, number])
            .setHTML(`
              <div style="padding: 12px; min-width: 220px;">
                <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 10px;">
                  <span style="
                    width: 12px;
                    height: 12px;
                    border-radius: 50%;
                    background: ${severityColor};
                    box-shadow: 0 0 8px ${severityColor};
                  "></span>
                  <span style="
                    font-size: 11px;
                    font-weight: 600;
                    text-transform: uppercase;
                    color: ${severityColor};
                    letter-spacing: 0.5px;
                  ">${props.severity} Alert</span>
                </div>
                <div style="font-weight: 600; font-size: 14px; margin-bottom: 8px; color: #fff;">${props.title}</div>
                <div style="font-size: 12px; color: #aaa; line-height: 1.4; margin-bottom: 10px;">${props.description}</div>
                <div style="font-size: 11px; color: #666; border-top: 1px solid #333; padding-top: 8px;">
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span>Type:</span>
                    <span style="text-transform: capitalize; color: #888;">${props.type}</span>
                  </div>
                  <div style="display: flex; justify-content: space-between;">
                    <span>Time:</span>
                    <span style="color: #888;">${timestamp}</span>
                  </div>
                </div>
              </div>
            `)
            .addTo(map);
        });

        // Change cursor on hover for alerts
        map.on("mouseenter", "alerts-center", () => {
          map.getCanvas().style.cursor = "pointer";
        });
        map.on("mouseleave", "alerts-center", () => {
          map.getCanvas().style.cursor = "";
        });

        // Click handler for nodes (2D circles at low zoom)
        map.on("click", "nodes-main", (e) => {
          if (!e.features?.[0]) return;

          const feature = e.features[0];
          const props = feature.properties;
          if (!props) return;
          const coords = (feature.geometry as GeoJSON.Point).coordinates;

          new mapboxgl.Popup({
            closeButton: true,
            closeOnClick: true,
            className: "node-popup",
          })
            .setLngLat(coords as [number, number])
            .setHTML(`
              <div style="padding: 8px; min-width: 180px;">
                <div style="font-weight: 600; font-size: 14px; margin-bottom: 8px;">${props.name}</div>
                <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 4px;">
                  <span style="width: 8px; height: 8px; border-radius: 50%; background: ${
                    props.status === "online" ? "#22c55e" : props.status === "degraded" ? "#f59e0b" : "#ef4444"
                  };"></span>
                  <span style="font-size: 12px; text-transform: capitalize;">${props.status}</span>
                </div>
                <div style="font-size: 11px; color: #888; margin-top: 8px;">
                  <div>Type: ${props.type}</div>
                  <div>Battery: ${props.batteryLevel}%</div>
                  <div>Signal: ${props.signalStrength}%</div>
                </div>
              </div>
            `)
            .addTo(map);
        });

        // Click handler for 3D node cylinders (at higher zoom)
        map.on("click", "nodes-3d", (e) => {
          if (!e.features?.[0]) return;

          const feature = e.features[0];
          const props = feature.properties;
          const coords = e.lngLat;

          new mapboxgl.Popup({
            closeButton: true,
            closeOnClick: true,
            className: "node-popup",
          })
            .setLngLat(coords)
            .setHTML(`
              <div style="padding: 8px; min-width: 180px;">
                <div style="font-weight: 600; font-size: 14px; margin-bottom: 8px;">${props?.name}</div>
                <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 4px;">
                  <span style="width: 8px; height: 8px; border-radius: 50%; background: ${
                    props?.status === "online" ? "#22c55e" : props?.status === "degraded" ? "#f59e0b" : "#ef4444"
                  };"></span>
                  <span style="font-size: 12px; text-transform: capitalize;">${props?.status}</span>
                </div>
                <div style="font-size: 11px; color: #888; margin-top: 8px;">
                  <div>Type: ${props?.type}</div>
                  <div>Battery: ${props?.batteryLevel}%</div>
                  <div>Signal: ${props?.signalStrength}%</div>
                </div>
              </div>
            `)
            .addTo(map);
        });

        // Change cursor on hover for nodes
        map.on("mouseenter", "nodes-main", () => {
          map.getCanvas().style.cursor = "pointer";
        });
        map.on("mouseleave", "nodes-main", () => {
          map.getCanvas().style.cursor = "";
        });
        map.on("mouseenter", "nodes-3d", () => {
          map.getCanvas().style.cursor = "pointer";
        });
        map.on("mouseleave", "nodes-3d", () => {
          map.getCanvas().style.cursor = "";
        });

        // Resize map when container size changes (e.g., sidebar toggle)
        resizeObserverRef.current = new ResizeObserver(() => {
          map.resize();
        });
        resizeObserverRef.current.observe(container.current!);

        setIsLoaded(true);
      });

      // Handle style changes (theme switch) - re-add layers after new style loads
      map.on("style.load", () => {
        // Skip the first style load (initial map creation), only handle subsequent style changes
        if (isFirstStyleLoadRef.current) {
          isFirstStyleLoadRef.current = false;
          return;
        }
        // Re-add layers after theme/style change
        addCustomLayers(map, currentThemeRef.current);
        startAnimations(map);
      });

      map.on("error", (e) => {
        console.error("Map error:", e);
        setMapError(e.error?.message || "Map error occurred");
      });

      mapRef.current = map;

      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
        if (rippleAnimationRef.current) {
          cancelAnimationFrame(rippleAnimationRef.current);
        }
        if (droneAnimationRef.current) {
          cancelAnimationFrame(droneAnimationRef.current);
        }
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
        }
        map.remove();
        mapRef.current = null;
        initializedRef.current = false;
      };
    } catch (error) {
      console.error("Map initialization error:", error);
      setMapError(error instanceof Error ? error.message : "Failed to initialize map");
    }
  }, [container, addCustomLayers, startAnimations]);

  useEffect(() => {
    if (!mapRef.current || !isLoaded) return;
    if (currentThemeRef.current === themeMode) return;

    currentThemeRef.current = themeMode;
    mapRef.current.setStyle(MAP_STYLES[themeMode]);
  }, [themeMode, isLoaded]);

  return {
    map: mapRef.current,
    isLoaded,
    mapError,
    alerts,
    flyToAlert,
    isDroneMode,
    startDroneAnimation,
    stopDroneAnimation,
    resetView,
    nodesVisible,
    setNodesVisible,
  };
};
